// Renglones de «Lo que se financió»: las líneas a crédito de la venta, sin las de contado.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { shownDiscountCode } from '@/shared/patterns/SalePromotion';

/** Con más renglones que esto, la card muestra los primeros `COLLAPSED_COUNT` y un «Ver N más». */
export const MAX_VISIBLE_LINES = 4;
export const COLLAPSED_COUNT = 3;

export interface FinancedLineView {
    lineId: string;
    name: string;
    sku: string;
    kind: 'equipo' | 'kit' | 'accesorio';
    /** Precio antes de descuentos; `null` cuando no hay nada que tachar. */
    originalCents: number | null;
    netCents: number;
    /** Promo de venta de la línea; ausente si no tuvo. */
    promotion?: { code?: string };
}

function kindOf(line: SaleLine): FinancedLineView['kind'] {
    if (line.product?.category === 'PHONE') return 'equipo';
    if (line.product?.category === 'BUNDLE') return 'kit';
    return 'accesorio';
}

function nameOf(line: SaleLine): string {
    const name = [line.product?.brand, line.product?.model].filter(Boolean).join(' ');
    return name === '' ? line.sku : name;
}

/** La línea tal como la pinta la card; solo tacha el precio previo a la promo. */
export function financedLineView(line: SaleLine): FinancedLineView {
    const netCents = line.basePriceCents * line.qty - (line.discountCents ?? 0);
    const original = line.originalPriceCents ?? null;
    const code = shownDiscountCode(line);
    return {
        lineId: line.lineId,
        name: nameOf(line),
        sku: line.sku,
        kind: kindOf(line),
        originalCents: original !== null && original > netCents ? original : null,
        netCents,
        ...((line.promotionId || code) && { promotion: code ? { code } : {} }),
    };
}

/** Solo lo que entró al crédito: fuera contado y regalos, en el orden de la venta. */
export function financedLinesOf(lines: readonly SaleLine[]): FinancedLineView[] {
    return lines
        .filter((l) => l.paymentType === 'CREDIT' && l.role !== 'GIFT')
        .map(financedLineView);
}
